import { memo, useState, useCallback } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Fetch real speed limit from backend (OSM + community data)
const _speedCache = {};
let _lastFetchTime = 0;
const FETCH_COOLDOWN = 5000;

function gridKey(lat, lng) {
  return `${lat.toFixed(3)}_${lng.toFixed(3)}`;
}

export async function fetchSpeedLimit(lat, lng) {
  const key = gridKey(lat, lng);
  if (_speedCache[key]) return _speedCache[key];
  const now = Date.now();
  if (now - _lastFetchTime < FETCH_COOLDOWN) return null;
  _lastFetchTime = now;
  try {
    const resp = await fetch(`${API}/speed-limit?lat=${lat}&lng=${lng}`);
    if (!resp.ok) return null;
    const data = await resp.json();
    const limit = data.speed_limit;
    if (limit) _speedCache[key] = limit;
    return limit;
  } catch {
    return null;
  }
}

export async function reportSpeedLimit(lat, lng, speedLimit, token) {
  try {
    const resp = await fetch(`${API}/speed-limit/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ lat, lng, speed_limit: speedLimit }),
    });
    if (!resp.ok) return null;
    return await resp.json();
  } catch {
    return null;
  }
}

const SPEED_OPTIONS = [20, 30, 40, 50, 60, 70];

// Speed limit report picker overlay
const SpeedReportPicker = memo(({ onSelect, onClose, currentSpeed }) => (
  <div className="absolute left-0 top-full mt-2 z-[600] bg-white/95 backdrop-blur-md border border-gray-200/60 rounded-xl p-3 shadow-2xl animate-in slide-in-from-top-2 duration-200" data-testid="speed-report-picker">
    <div className="flex items-center gap-1.5 mb-2">
      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
      <span className="text-[11px] font-bold text-gray-800">What's the correct limit?</span>
      <button onClick={onClose} className="ml-auto p-0.5 rounded hover:bg-gray-100">
        <X className="w-3 h-3 text-gray-400" />
      </button>
    </div>
    <div className="grid grid-cols-3 gap-1.5">
      {SPEED_OPTIONS.map(s => (
        <button
          key={s}
          onClick={() => onSelect(s)}
          data-testid={`speed-report-${s}`}
          className={`w-11 h-11 rounded-full border-[3px] flex items-center justify-center text-sm font-extrabold transition-all ${
            s === currentSpeed
              ? 'border-gray-300 bg-gray-100 text-gray-400'
              : 'border-red-600 bg-white text-gray-900 hover:scale-110 active:scale-95 shadow-sm'
          }`}
        >
          {s}
        </button>
      ))}
    </div>
    <p className="text-[9px] text-gray-400 mt-2 text-center">3 matching reports to confirm</p>
  </div>
));
SpeedReportPicker.displayName = 'SpeedReportPicker';

// Confirmation toast
const ReportToast = memo(({ result, onDone }) => {
  if (!result) return null;
  return (
    <div className="absolute left-0 top-full mt-2 z-[600] bg-white/95 backdrop-blur-md border border-gray-200/60 rounded-xl p-3 shadow-2xl animate-in slide-in-from-top-2 duration-200" data-testid="speed-report-toast">
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4 text-green-500" />
        <div>
          <p className="text-xs font-bold text-gray-800">
            {result.confirmed ? 'Confirmed!' : 'Reported!'}
          </p>
          <p className="text-[10px] text-gray-500">
            {result.confirmed
              ? `${result.speed_limit} mph now live for everyone`
              : `${result.matching_reports}/${3} reports — ${result.needed} more needed`}
          </p>
        </div>
      </div>
    </div>
  );
});
ReportToast.displayName = 'ReportToast';

// Main speed limit sign — tappable to report
const SpeedLimitSign = memo(({ speedLimit, lat, lng, token, className = '' }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [reportResult, setReportResult] = useState(null);

  const handleTap = useCallback(() => {
    setReportResult(null);
    setShowPicker(prev => !prev);
  }, []);

  const handleReport = useCallback(async (newSpeed) => {
    setShowPicker(false);
    if (newSpeed === speedLimit) return;
    if (!token || !lat || !lng) return;
    const result = await reportSpeedLimit(lat, lng, newSpeed, token);
    if (result) {
      setReportResult(result);
      // Clear the grid cache so next fetch picks up the new value
      const key = gridKey(lat, lng);
      delete _speedCache[key];
      setTimeout(() => setReportResult(null), 4000);
    }
  }, [speedLimit, lat, lng, token]);

  if (!speedLimit) return null;

  return (
    <div className={`relative ${className}`}>
      {/* Tappable sign */}
      <button
        onClick={handleTap}
        data-testid="speed-limit-sign"
        className="flex items-center justify-center cursor-pointer"
        style={{
          width: 56, height: 56, borderRadius: '50%',
          border: '4px solid #cc0000', background: '#ffffff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        <span style={{
          fontSize: speedLimit >= 100 ? 18 : 22,
          fontWeight: 800, color: '#1a1a1a', lineHeight: 1,
          fontFamily: 'Arial, Helvetica, sans-serif',
        }}>
          {speedLimit}
        </span>
      </button>
      {/* Small report hint */}
      <button
        onClick={handleTap}
        className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-zinc-800 border border-zinc-600 flex items-center justify-center"
        data-testid="speed-report-btn"
      >
        <AlertTriangle className="w-2.5 h-2.5 text-amber-400" />
      </button>
      {/* Picker */}
      {showPicker && (
        <SpeedReportPicker
          currentSpeed={speedLimit}
          onSelect={handleReport}
          onClose={() => setShowPicker(false)}
        />
      )}
      {/* Result toast */}
      {reportResult && <ReportToast result={reportResult} />}
    </div>
  );
});

SpeedLimitSign.displayName = 'SpeedLimitSign';
export default SpeedLimitSign;
